// ProgressLoader — a rule-of-thumb component for ANY long-running AI/server
// operation in the admin console. Always show one of these instead of a static
// spinner so the user knows the work is genuinely in progress.
//
// Usage:
//   <ProgressLoader
//     active={busy}
//     title="Generating 10 emails"
//     stages={["Loading codebase inventory", "Drafting Day 1", ...]}
//     stageIntervalMs={4000}
//   />
//
// Behavior: cycles through `stages` advancing every `stageIntervalMs`.
// Once the last stage is reached it stays there with a "(finishing up...)" hint
// so we never lie about being done. Also shows elapsed wall-clock time.

import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";

export interface ProgressLoaderProps {
  active: boolean;
  title: string;
  stages: string[];
  stageIntervalMs?: number;
  /** Optional one-line note shown above the stage list */
  note?: string;
}

export default function ProgressLoader({ active, title, stages, stageIntervalMs = 4000, note }: ProgressLoaderProps) {
  const [stageIdx, setStageIdx] = useState(0);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!active) { setStageIdx(0); setStartedAt(null); return; }
    setStartedAt(Date.now());
    const stageT = setInterval(() => {
      setStageIdx((i) => Math.min(stages.length - 1, i + 1));
    }, stageIntervalMs);
    const tickT = setInterval(() => setNow(Date.now()), 500);
    return () => { clearInterval(stageT); clearInterval(tickT); };
  }, [active, stageIntervalMs, stages.length]);

  if (!active) return null;
  const elapsedSec = startedAt ? Math.floor((now - startedAt) / 1000) : 0;
  const onLast = stageIdx === stages.length - 1;

  return (
    <div className="rounded-lg border border-[#FF5C00]/30 bg-[#FF5C00]/5 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-[#FF5C00]" />
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">{elapsedSec}s</span>
      </div>
      {note && <p className="text-xs text-muted-foreground">{note}</p>}
      <ul className="space-y-1">
        {stages.map((s, i) => {
          const done = i < stageIdx;
          const current = i === stageIdx;
          return (
            <li key={i} className={`flex items-center gap-2 text-xs ${done ? "text-emerald-700" : current ? "text-[#FF5C00] font-medium" : "text-muted-foreground/60"}`}>
              {done ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                    : current ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                    : <span className="w-3.5 h-3.5 inline-block rounded-full border border-current opacity-40 shrink-0" />}
              <span>{s}{current && onLast ? " (finishing up...)" : ""}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
